import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, ArrowUp } from 'lucide-react';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { useBusinessInfo, telHref } from '../hooks/useBusinessInfo';
import { useSiteFeatureFlags } from '../hooks/useSiteFeatureFlags';
import TransferSiteFooter from '../tools/transfer/TransferSiteFooter';

const Footer = () => {
  const { siteName, logoUrl, supportEmail, logoHeight } = useSiteSettings();
  // Phone comes from Settings -> Business Info, never a literal in the markup.
  const { phone } = useBusinessInfo();
  const { transfer_site_enabled, showcase_enabled } = useSiteFeatureFlags();
  const phoneHref = telHref(phone);
  const currentYear = new Date().getFullYear();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const footerLinks = {
    explore: [
      ...(showcase_enabled === true ? [{ label: 'Portfolio', href: '/apps' }] : []),
      { label: 'Free Tools', href: '/tools' },
      { label: 'Project Estimator', href: '/#estimator' },
      { label: 'Schedule a Consultation', href: '/schedule-a-demo' },
    ],
    industries: [
      { label: 'Retail', href: '/industries/retail-uses' },
      { label: 'Warehouses', href: '/industries/warehouses' },
      { label: 'Hospitality', href: '/industries/hospitality' },
      { label: 'Education', href: '/industries/education' },
      { label: 'Healthcare', href: '/industries/healthcare' },
    ],
    company: [
      { label: 'About Us', href: '/about' },
      { label: 'Resources', href: '/123-bots-resources' },
      { label: 'Contact', href: '/contact' },
      { label: 'FAQ', href: '/faq' },
      { label: 'Careers', href: '/careers' },
    ],
    support: [
      { label: 'Schedule a Demo', href: '/schedule-a-demo' },
      { label: 'Parts & Accessories', href: '/shop?category=parts' },
      { label: 'Technical Support', href: '/contact' },
    ],
    legal: [
      { label: 'Privacy Policy', href: '/privacy-policy' },
      { label: 'Terms & Conditions', href: '/terms-conditions' },
      { label: 'Accessibility', href: '/accessibility' },
    ],
  };

  // Transfer Site template: one slim row instead of the CRM footer (these are dark pages).
  if (transfer_site_enabled) return <TransferSiteFooter dark />;

  return (
    <footer className="bg-bots-darker border-t border-gray-800" data-testid="main-footer">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8">
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-3 lg:col-span-1">
            <Link to="/" className="inline-block mb-4">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={siteName || 'Home'}
                  style={{ height: `${logoHeight}px` }}
                  className="w-auto object-contain"
                />
              ) : (
                <span className="text-2xl font-bold text-white">{siteName}</span>
              )}
            </Link>
            <p className="text-gray-400 text-sm mb-4">
              Custom software, web platforms and automation built for businesses that have outgrown off-the-shelf tools.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-2">
              {phoneHref && (
                <a
                  href={phoneHref}
                  className="flex items-center text-gray-400 hover:text-white transition-colors text-sm"
                  data-testid="footer-phone"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  {phone}
                </a>
              )}
              {supportEmail && (
                <a
                  href={`mailto:${supportEmail}`}
                  className="flex items-center text-gray-400 hover:text-white transition-colors text-sm"
                  data-testid="footer-email"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  {supportEmail}
                </a>
              )}
            </div>
          </div>

          {/* Explore */}
          <div>
            <h4 className="text-white font-semibold mb-4">Explore</h4>
            <ul className="space-y-2">
              {footerLinks.explore.map((link) => (
                <li key={link.label}>
                  <Link 
                    to={link.href}
                    className="text-gray-400 hover:text-white transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Industries */}
          <div>
            <h4 className="text-white font-semibold mb-4">Industries</h4>
            <ul className="space-y-2">
              {footerLinks.industries.map((link) => (
                <li key={link.label}>
                  <Link 
                    to={link.href}
                    className="text-gray-400 hover:text-white transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-white font-semibold mb-4">Company</h4>
            <ul className="space-y-2">
              {footerLinks.company.map((link) => (
                <li key={link.label}>
                  <Link 
                    to={link.href}
                    className="text-gray-400 hover:text-white transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="text-white font-semibold mb-4">Support</h4>
            <ul className="space-y-2">
              {footerLinks.support.map((link) => (
                <li key={link.label}>
                  <Link 
                    to={link.href}
                    className="text-gray-400 hover:text-white transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-white font-semibold mb-4">Legal</h4>
            <ul className="space-y-2">
              {footerLinks.legal.map((link) => (
                <li key={link.label}>
                  <Link 
                    to={link.href}
                    className="text-gray-400 hover:text-white transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-500 text-sm">
              © {currentYear} {siteName}. All rights reserved.
            </p>
            
            {/* Back to Top Button */}
            <button
              onClick={scrollToTop}
              className="mt-4 md:mt-0 flex items-center text-gray-400 hover:text-white transition-colors text-sm"
              data-testid="back-to-top"
            >
              Back to top
              <ArrowUp className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
